import { Link } from 'react-router-dom'

import {
  Card,
  CardBody,
  CardHead,
  LoadError,
  Skeleton,
  Stat,
} from '../components/ui.tsx'
import { UpcomingGames } from '../components/games.tsx'
import { SignalCard } from '../components/signals.tsx'
import { usePageTitle } from '../layout/AppShell.tsx'
import { useApi } from '../lib/api.ts'
import { gbp, pnl, toKickOff } from '../lib/format.ts'
import type { HomePayload, PositionRow } from '../../shared/types.ts'

/**
 * Today: the games coming up on the left, and on the right where we stand — P&L,
 * liability, open positions — and which prices are moving.
 */
export function Home() {
  usePageTitle('Today')
  const { data, error, loading, reload } = useApi<HomePayload>('/home', 5000)

  if (error) return <main className="content"><LoadError error={error} onRetry={reload} /></main>

  return (
    <main className="content">
      <div className="grid cols-12">
        {/* LEFT: what is coming up */}
        <div className="span-8 stack">
          <UpcomingGames />
        </div>

        {/* RIGHT: where we stand, and what is moving */}
        <div className="span-4 stack">
          {loading || !data ? (
            [0, 1, 2].map((i) => (
              <Card pad key={i}>
                <Skeleton rows={2} />
              </Card>
            ))
          ) : (
            <>
              <Card pad>
                <Stat
                  label="Today · settled bets"
                  tone={data.pnl.realised > 0 ? 'pos' : data.pnl.realised < 0 ? 'neg' : null}
                  value={pnl(data.pnl.realised)}
                  sub="Open bets count once their game is settled"
                />
              </Card>

              <Card pad>
                <Stat
                  label="Total liability at risk now"
                  value={gbp(data.liability.total)}
                  sub="What your open bets stand to lose"
                />
              </Card>

              <Card>
                <CardHead
                  title={`Open positions · ${data.openPositions.count}`}
                  right={
                    <Link className="btn ghost sm" to="/positions">
                      All →
                    </Link>
                  }
                />
                <CardBody flush>
                  {data.positions.length === 0 && data.openPositions.accumulators === 0 ? (
                    <div style={{ padding: '14px 18px' }} className="u-faint">
                      Nothing open. {data.openPositions.workingOrders > 0
                        ? `${data.openPositions.workingOrders} order${data.openPositions.workingOrders === 1 ? '' : 's'} working.`
                        : ''}
                    </div>
                  ) : (
                    data.positions.map((p, i) => (
                      <PositionSummaryRow
                        key={p.marketId}
                        position={p}
                        last={i === data.positions.length - 1 && data.openPositions.accumulators === 0}
                      />
                    ))
                  )}
                  {data.openPositions.accumulators > 0 && (
                    <Link to="/positions" className="cell-sub" style={{ display: 'block', padding: '12px 18px' }}>
                      {data.openPositions.accumulators} accumulator{data.openPositions.accumulators === 1 ? '' : 's'} open →
                    </Link>
                  )}
                </CardBody>
              </Card>

              {data.signals.length > 0 && (
                <Card>
                  <CardHead
                    title="Moving now"
                    right={
                      <Link className="btn ghost sm" to="/markets">
                        All markets →
                      </Link>
                    }
                  />
                  <CardBody className="stack sm">
                    {data.signals.map((s) => (
                      <SignalCard key={`${s.marketId}:${s.selectionId}:${s.kind}`} signal={s} />
                    ))}
                  </CardBody>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  )
}

/** One position, compact enough for the side column. Opens its market. */
function PositionSummaryRow({ position: p, last }: { position: PositionRow; last: boolean }) {
  const kick = toKickOff(p.startTime)
  const state = p.suspended ? 'Suspended' : p.inPlay ? `In-play${p.minute ? ` ${p.minute}'` : ''}` : kick.label

  return (
    <Link
      to={`/markets/${p.marketId}`}
      className="between"
      style={{
        padding: '12px 18px',
        color: 'inherit',
        borderBottom: last ? undefined : '1px solid var(--border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="cell-strong">{p.event}</div>
        <div className="cell-sub">
          {p.marketType} · {state}
        </div>
      </div>
      <div className="u-right">
        <div className="u-mono">{gbp(p.liability)}</div>
      </div>
    </Link>
  )
}
